import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import { SnackContext } from 'src/store/ContextStore';
import CustomDialog from 'src/components/UI/dialog/CustomDialog';
import useDateFormatter from 'src/hooks/useDateFormatter';
import { toLocalDateFromInstant } from 'src/util/partialDate';
import {
  describeTestResultError,
  useLabReportsForTest
} from 'src/hooks/useStructuredTestResults';
import { useLabReportWithdrawal } from 'src/hooks/useLabReportWithdrawal';
import { CONCLUSION_LABELS, reportDateText } from './labReportDisplay';
import { individualLabel } from 'src/components/lab-report-inbox/labReportInboxDisplay';
import { roleOfIndividualKey } from '../clinical-notes-section/labReportInboxLink';
import { FIELD_LABELS } from './applyProposal';
import { openReportInNewTab } from 'src/components/lab-report-verification/reportSources';
import { useAutoTranslation } from 'src/hooks/useTranslate';

// The lab reports applied to a Test, in its Result section (TGCLOUD-1866, Phase 2 rework): each
// report's record (GET /test-results/by-test/:testNoteId), with "Open report" for the stored file
// and "Mark entered in error" for one that should never have been applied (a duplicate, someone
// else's report).
//
// WITHDRAWING REMOVES WHAT CAME ONLY FROM THE REPORT (backend labReportWithdrawal.js): the Test
// fields it filled, the genes and variants it added, and the genotype variants its copy created,
// where nobody has changed them since and nothing else accounts for them. What the report REPLACED
// (a variant it was merged into, a gene row it filled, a value chosen over the Test's) is GIVEN
// BACK as the Test held it, and a gene row that stays is released from the report (its coverage no
// longer stated). Everything else is left and named for review. The dialog says so first, and
// afterwards says exactly what was removed, what was given back and what to review. The Test form takes the answer (onWithdrawn), and the person's genotypes go onto
// their chart row (useLabReportWithdrawal), so neither a later Save nor the pedigree autosave
// writes the removed values back.
//
// A read-only member sees the list and can open a report; nothing that writes is offered. Nothing
// is drawn for a Test with no report, or while the record's group has the feature off.
//
// "OPEN REPORT" (TGCLOUD-1868, plan D6) opens the verification view on the report, read-only,
// when the Test form offers it (`onOpenReport`): the report beside the Test as saved, with where
// each value is printed and how each part was checked. "Open in a new tab" is the signed link it
// always opened, kept beside it. Without `onOpenReport` (a report is being checked on the Test
// already), "Open report" is that signed link, as before.
//
// WHY IT IS IN ERROR (Phase 3, "Undo after linking"). The clinician says which, with NO default:
// someone else's report goes back to the lab report inbox as the wrong patient (this person is
// rejected on the item, so it is matched to the right one); this person's report on the wrong Test
// goes back still matched to them, for the right Test; or it is withdrawn only. The withdrawal is
// the same for all three. The outcome says where the report went, and a return the server could not
// finish is offered again (the route finishes whatever a stopped run left).

const PANEL_SX = {
  p: 2,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1
};
const VERBATIM_SX = { whiteSpace: 'pre-wrap', wordBreak: 'break-word' };
const CAPTION_SX = { ...VERBATIM_SX, color: 'text.secondary' };

const STATUS = Object.freeze({
  current: { label: 'Applied', color: 'success' },
  superseded: { label: 'Superseded', color: 'default' },
  'entered-in-error': { label: 'Entered in error', color: 'error' }
});

// Why a withdrawal left something, in words (backend labReportWithdrawal.js reasons).
export const REVIEW_REASONS = Object.freeze({
  EDITED: 'changed since the report was applied',
  TEST_NAME_KEPT: 'a Test keeps its name',
  GENE_HAS_VARIANTS: 'a variant in this gene is still on the Test',
  NOT_ADDED_BY_THE_REPORT: 'on the Test before the report was applied',
  LISTED_BY_ANOTHER_TEST: 'another Test lists this variant',
  CLINICIAN_INTERPRETATION: 'a clinician has interpreted it',
  DISCREPANCY_REVIEWED: 'a discrepancy review is recorded on it',
  ATTRIBUTED_ELSEWHERE: 'its call now comes from another source',
  FROM_WITHDRAWN_REPORT: 'its call still names this report',
  HELD_BEFORE_THE_REPORT:
    'on the genotype record before the report was applied, and its call is still the report’s',
  REPLACED_A_WITHDRAWN_REPORT:
    'removed: it had replaced a variant from a report also withdrawn, so check what the Test should hold',
  // TGCLOUD-1868: what the gene rows are said to be.
  COMPLETE_LIST_CHANGED:
    'it said every gene the laboratory analysed is listed, which the withdrawal changed: say again what the gene list is',
  // TGCLOUD-1868, decision 13: never "complete" for an exome or a genome.
  COMPLETE_LIST_SCOPE:
    'a Test of an exome or a genome cannot say every gene the laboratory analysed is listed, so it now says only some of the genes are recorded',
  // TGCLOUD-1868, decision 16: a gene typed by hand from the report's gene list, kept because it is
  // the clinician's entry.
  GENES_ENTERED_FROM_REPORT:
    'entered by hand from this report’s gene list when it was applied, and kept: check it still belongs on this Test'
});

// What a report added to its Test, counted: a phrase for one and one for more.
const ADDED_TEXT = Object.freeze({
  field: ['1 field', '{{count}} fields'],
  gene: ['1 gene', '{{count}} genes'],
  variant: ['1 variant', '{{count}} variants']
});
const countText = (n, kind, t) =>
  n === 1 ? t(ADDED_TEXT[kind][0]) : t(ADDED_TEXT[kind][1], { count: n });

// The server's withdrawal reasons (backend rules.WITHDRAWAL_REASONS), in the dialog's order.
export const WITHDRAWAL_REASON_CHOICES = Object.freeze([
  {
    reason: 'wrong-patient',
    label: 'Back to the Lab Report Inbox: wrong patient',
    description:
      'It is someone else’s report. It goes back to the inbox with this person recorded as not its patient, to be matched to the right one.'
  },
  {
    reason: 'wrong-test',
    label: 'Back to the Lab Report Inbox: wrong Test',
    description:
      'It is this person’s report, for another of their Tests. It goes back to the inbox still matched to this person, to be applied to the right Test.'
  },
  {
    reason: 'withdrawn',
    label: 'Withdrawn only',
    description:
      'It should not have been applied at all. It is marked entered in error and does not go back to the inbox.'
  }
]);

// An inbox item's state, as the outcome says it.
const INBOX_STATE_TEXT = Object.freeze({
  open: 'waiting to be matched to a patient',
  matched: 'matched to a patient, waiting to be applied to a Test',
  linked: 'applied to a Test',
  dismissed: 'dismissed'
});

// Where a report withdrawn as the wrong patient or the wrong Test went (the answer's `inbox`).
function InboxOutcome({ answer, retrying, onRetry }) {
  const t = useAutoTranslation();

  const inbox = answer?.inbox;
  if (!inbox || answer?.reason === 'withdrawn') return null;
  if (inbox.returned === false) {
    return (
      <Alert
        severity="warning"
        sx={{ mt: 1.5 }}
        data-testid="withdrawal-inbox-outcome"
        action={
          <Button
            color="inherit"
            size="small"
            onClick={onRetry}
            disabled={retrying}
            startIcon={retrying ? <CircularProgress size={14} /> : null}
          >
            {t('Try again')}
          </Button>
        }
      >
        {t(
          'The report is marked entered in error, but it was not put back in the Lab Report Inbox. Try again to finish.'
        )}
      </Alert>
    );
  }
  const lines = [];
  if (answer.reason === 'wrong-patient' && inbox.state === 'open') {
    lines.push(
      t(
        'The report is back in the Lab Report Inbox, waiting to be matched to the right patient. This person is recorded there as not its patient.'
      )
    );
  } else if (answer.reason === 'wrong-test' && inbox.state === 'matched') {
    lines.push(
      t(
        'The report is back in the Lab Report Inbox, still matched to this person, to be applied to the right Test.'
      )
    );
  } else if (answer.reason === 'wrong-test' && inbox.state === 'open') {
    lines.push(
      t(
        'The report is back in the Lab Report Inbox, waiting to be matched: it had not come from the inbox, so nobody is matched to it yet.'
      )
    );
  } else {
    lines.push(
      t('The report’s Lab Report Inbox item is {{state}}.', {
        state: t(INBOX_STATE_TEXT[inbox.state] || 'in the inbox')
      })
    );
  }
  if (inbox.created === true && answer.reason === 'wrong-patient') {
    lines.push(
      t(
        'It had not come from the inbox, so it is a new inbox item made from the stored report.'
      )
    );
  } else if (inbox.created === false) {
    // A record that never came from the inbox, whose report was already waiting there.
    lines.push(t('The same report was already waiting in the inbox.'));
  }
  return (
    <Alert
      severity="info"
      sx={{ mt: 1.5 }}
      data-testid="withdrawal-inbox-outcome"
    >
      {lines.map((line) => (
        <Typography key={line} variant="body2" sx={VERBATIM_SX}>
          {line}
        </Typography>
      ))}
    </Alert>
  );
}

const variantText = (v) => [v.gene, v.variantC].filter(Boolean).join(' ');

// "Gene BRCA2: changed since the report was applied": one thing a withdrawal left, and why. The
// reason is one of REVIEW_REASONS (a code the server adds is shown as it is).
const reviewItemText = (item, t) => {
  const what =
    item.kind === 'field'
      ? t(FIELD_LABELS[item.field]) || item.field
      : item.kind === 'gene'
      ? t('Gene {{gene}}', { gene: item.gene })
      : item.kind === 'variant'
      ? t('Variant {{variant}}', { variant: variantText(item) })
      : t('Genotype record: {{variant}}', { variant: variantText(item) });
  return `${what}: ${
    REVIEW_REASONS[item.reason] ? t(REVIEW_REASONS[item.reason]) : item.reason
  }`;
};

// What the open form has changed, and not saved, of the rows from the report about to be withdrawn
// (applyProposal.unsavedRowsFromReport). The withdrawal takes those rows back to what the server
// leaves, changes and all, so the dialog says so first (review of the decisions, finding 4).
function UnsavedChangesWarning({ items }) {
  const t = useAutoTranslation();

  if (!items || !items.length) return null;
  const rows = items
    .map((item) =>
      item.kind === 'gene'
        ? t('Gene {{gene}}', { gene: item.gene })
        : t('Variant {{variant}}', { variant: variantText(item) })
    )
    .join('; ');
  return (
    <Alert
      severity="warning"
      sx={{ mb: 2 }}
      data-testid="withdrawal-unsaved-changes"
    >
      <Typography variant="body2" sx={VERBATIM_SX}>
        {t(
          'You have changed, and not saved, what this Test holds from this report: {{rows}}.',
          { rows }
        )}
      </Typography>
      <Typography variant="body2" sx={{ ...VERBATIM_SX, mt: 0.5 }}>
        {t(
          'Marking the report entered in error can take these off the form, or back to what the Test held before the report, and your changes with them. To keep your changes, cancel and save the Test first: the withdrawal then leaves them for you to review.'
        )}
      </Typography>
    </Alert>
  );
}

function WithdrawalOutcome({ answer }) {
  const t = useAutoTranslation();

  const removed = answer?.removed || {};
  const restored = answer?.restored || {};
  const detached = answer?.detached || {};
  const toReview = Array.isArray(answer?.toReview) ? answer.toReview : [];
  const fieldsText = (fields) =>
    fields.map((f) => t(FIELD_LABELS[f]) || f).join(', ');
  const givenBack = [
    (restored.fields || []).length
      ? t('Test fields: {{fields}}', { fields: fieldsText(restored.fields) })
      : null,
    (restored.genesTested || []).length
      ? t('Genes tested: {{genes}}', {
          genes: restored.genesTested.map((g) => g.gene).join(', ')
        })
      : null,
    (restored.variants || []).length
      ? t('Variants: {{variants}}', {
          variants: restored.variants.map(variantText).join('; ')
        })
      : null,
    (restored.genotypeVariants || []).length
      ? t('Genotype record, the Test’s own call again: {{variants}}', {
          variants: restored.genotypeVariants.map(variantText).join('; ')
        })
      : null,
    (detached.genesTested || []).length
      ? t(
          'Genes tested no longer from this report, coverage not stated: {{genes}}',
          { genes: detached.genesTested.map((g) => g.gene).join(', ') }
        )
      : null
  ].filter(Boolean);
  const lines = [
    (removed.fields || []).length
      ? t('Test fields: {{fields}}', { fields: fieldsText(removed.fields) })
      : null,
    (removed.genesTested || []).length
      ? t('Genes tested: {{genes}}', {
          genes: removed.genesTested.join(', ')
        })
      : null,
    (removed.variants || []).length
      ? t('Variants: {{variants}}', {
          variants: removed.variants.map(variantText).join('; ')
        })
      : null,
    (removed.genotypeVariants || []).length
      ? t('Genotype record: {{variant}}', {
          variant: removed.genotypeVariants.map(variantText).join('; ')
        })
      : null
  ].filter(Boolean);
  return (
    <Box data-testid="withdrawal-outcome">
      <Typography sx={{ fontWeight: 700 }}>{t('Removed')}</Typography>
      {lines.length ? (
        lines.map((line) => (
          <Typography key={line} variant="body2" sx={VERBATIM_SX}>
            {line}
          </Typography>
        ))
      ) : (
        <Typography variant="body2">
          {givenBack.length ? t('Nothing.') : t('Nothing: see below.')}
        </Typography>
      )}
      {givenBack.length > 0 && (
        <Box sx={{ mt: 1.5 }} data-testid="withdrawal-given-back">
          <Typography sx={{ fontWeight: 700 }}>
            {t('Given back as the Test held it before the report')}
          </Typography>
          {givenBack.map((line) => (
            <Typography key={line} variant="body2" sx={VERBATIM_SX}>
              {line}
            </Typography>
          ))}
        </Box>
      )}
      {toReview.length > 0 && (
        <Box sx={{ mt: 1.5 }} data-testid="withdrawal-to-review">
          <Typography sx={{ fontWeight: 700 }}>
            {t('Left for you to review ({{count}})', {
              count: toReview.length
            })}
          </Typography>
          {toReview.map((item, i) => (
            <Typography key={i} variant="body2" sx={VERBATIM_SX}>
              {reviewItemText(item, t)}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}

function LabReportCard({
  exported,
  readOnly,
  opening,
  onOpen,
  onOpenInNewTab,
  onWithdraw
}) {
  const t = useAutoTranslation();

  const fmtDate = useDateFormatter();
  const record = exported?.labReport || {};
  const proposal = record.extracted?.proposal || {};
  const provenance = record.provenance || {};
  const status = STATUS[record.recordStatus] || STATUS.current;
  const contributed = record.contributed || {};
  const identifiers = Array.isArray(exported?.identifiers)
    ? exported.identifiers
    : [];
  // confirmedAt is an INSTANT (the server's clock at the save that applied the report), read by
  // its local day. The report's own date is a calendar date and goes to fmtDate as it is.
  const appliedOn = provenance.confirmedAt
    ? fmtDate(toLocalDateFromInstant(provenance.confirmedAt))
    : '';
  const heading = [reportDateText(record.reportDate, fmtDate), proposal.lab]
    .filter(Boolean)
    .join(' · ');
  const conclusion = record.conclusion || {};
  // One person's part of a report about several. A single-fetus report's record is keyed to its fetus
  // from the inbox (decision 32) but read whole: its conclusion is the fetus's own result, so it reads
  // as one applied by an upload on the fetus's Test (`singleFetus`; TGCLOUD-1868 closeout review).
  const forWhom =
    typeof record.individualKey === 'string' &&
    record.individualKey &&
    record.singleFetus !== true
      ? {
          key: record.individualKey,
          role: roleOfIndividualKey(record.individualKey)
        }
      : null;
  const added = [
    (contributed.fields || []).length
      ? countText(contributed.fields.length, 'field', t)
      : null,
    (contributed.genes || []).length
      ? countText(contributed.genes.length, 'gene', t)
      : null,
    (contributed.findings || []).length
      ? countText(contributed.findings.length, 'variant', t)
      : null
  ].filter(Boolean);
  // "Applied by Dr Hale on 12/03/2026. It added 1 field, 2 genes to this Test.": one sentence for
  // each of what is known. The name is the record's.
  const appliedBy = provenance.confirmedByName || t('a clinician');
  const appliedByText = added.length
    ? appliedOn
      ? t('Applied by {{name}} on {{date}}. It added {{added}} to this Test.', {
          name: appliedBy,
          date: appliedOn,
          added: added.join(', ')
        })
      : t('Applied by {{name}}. It added {{added}} to this Test.', {
          name: appliedBy,
          added: added.join(', ')
        })
    : appliedOn
    ? t('Applied by {{name}} on {{date}}.', {
        name: appliedBy,
        date: appliedOn
      })
    : t('Applied by {{name}}.', { name: appliedBy });

  return (
    <Box data-testid="lab-report-record" sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, flex: 1 }}>
          {heading || t('Lab report')}
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          color={status.color}
          label={t(status.label)}
          sx={{ height: 20, fontSize: '0.7rem' }}
        />
      </Box>
      {proposal.testName && (
        <Typography variant="caption" component="div" sx={CAPTION_SX}>
          {proposal.testName}
        </Typography>
      )}
      {/* One person's part of a report about several (TGCLOUD-1868 review): whose it is, and that
          the conclusion below is the whole report's (on a parent's Test, the fetus's), never this
          person's own result, which is the Test's result summary. */}
      {forWhom && (
        <Typography
          variant="caption"
          component="div"
          sx={CAPTION_SX}
          data-testid="lab-report-record-for"
        >
          {t(
            'This record is for: {{individualLabel}}. The conclusion is the whole report’s, not this person’s own result.',
            { individualLabel: individualLabel(forWhom) }
          )}
        </Typography>
      )}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 1 }}>
        <Typography variant="body2" sx={{ ...VERBATIM_SX, flex: 1 }}>
          {forWhom && (
            <Box component="span" sx={{ fontWeight: 700 }}>
              {t('The report’s conclusion (the whole report):')}{' '}
            </Box>
          )}
          {conclusion.withheld
            ? t(
                'The conclusion named the patient, so this record does not keep its wording.'
              )
            : conclusion.asPrinted || t('The report states no conclusion.')}
        </Typography>
        <Chip
          label={
            t(CONCLUSION_LABELS[conclusion.normalised]) ||
            t(CONCLUSION_LABELS['not-stated'])
          }
          size="small"
          variant="outlined"
          sx={{ height: 20, fontSize: '0.7rem' }}
        />
      </Box>
      {identifiers.length > 0 && (
        <Typography variant="caption" component="div" sx={CAPTION_SX}>
          {identifiers
            .map((id) =>
              [id.labelAsPrinted, id.value].filter(Boolean).join(': ')
            )
            .join(' · ')}
        </Typography>
      )}
      <Typography
        variant="caption"
        component="div"
        sx={{ color: 'text.secondary', mt: 0.5 }}
        data-testid="applied-by"
      >
        {appliedByText}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
        {!readOnly && record.recordStatus === 'current' && (
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={onWithdraw}
          >
            {t('Mark entered in error')}
          </Button>
        )}
        {onOpenInNewTab && (
          <Button
            size="small"
            variant="text"
            onClick={onOpenInNewTab}
            disabled={opening}
            startIcon={<OpenInNewIcon />}
          >
            {t('Open in a new tab')}
          </Button>
        )}
        <Button
          size="small"
          variant="outlined"
          onClick={onOpen}
          disabled={opening}
          startIcon={
            opening ? (
              <CircularProgress size={14} />
            ) : onOpenInNewTab ? (
              <ArticleOutlinedIcon />
            ) : (
              <OpenInNewIcon />
            )
          }
        >
          {t('Open report')}
        </Button>
      </Box>
    </Box>
  );
}

/**
 * @param {object} props
 * @param {string} props.testNoteId  the SAVED Test's id
 * @param {string} props.familyMemberId
 * @param {boolean} props.isProband
 * @param {boolean} [props.readOnly]
 * @param {(answer: object, labReportId: string) => void} [props.onWithdrawn]  the Test form takes
 *   the answer's Test fields
 * @param {(labReportId: string) => Array<object>} [props.unsavedChangesFor]  what the open form
 *   has changed, and not saved, of the rows from a report (applyProposal.unsavedRowsFromReport)
 * @param {(labReportId: string, exported: object) => void} [props.onOpenReport]  "Open report"
 *   opens the verification view on the record (TGCLOUD-1868 D6); without it, the signed link
 */
export default function LabReportsList({
  testNoteId,
  familyMemberId,
  isProband,
  readOnly = false,
  onWithdrawn,
  unsavedChangesFor,
  onOpenReport
}) {
  const t = useAutoTranslation();

  const { setSnack } = React.useContext(SnackContext);
  const { labReports, error, off } = useLabReportsForTest(testNoteId, {
    enabled: !!testNoteId
  });
  const { withdraw } = useLabReportWithdrawal({ familyMemberId, isProband });
  const [opening, setOpening] = React.useState(null);
  const [withdrawing, setWithdrawing] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [outcome, setOutcome] = React.useState(null);
  // Why it is in error: chosen in the dialog, never defaulted.
  const [reason, setReason] = React.useState(null);

  if (!testNoteId || off) return null;
  if (!labReports.length && !error && !withdrawing) return null;

  // The signed link in a new tab.
  const openReport = async (id) => {
    setOpening(id);
    try {
      await openReportInNewTab(id);
    } catch (err) {
      setSnack({
        status: true,
        msg: describeTestResultError(err, t('The report could not be opened.'))
          .message,
        severity: 'error'
      });
    } finally {
      setOpening(null);
    }
  };

  // A retry (the same reason again) finishes a return the server could not finish. What the first
  // answer said was removed stays on screen: asked again, the server removes nothing more.
  const markEnteredInError = async () => {
    const id = withdrawing;
    if (!id || !reason) return;
    setBusy(true);
    try {
      const answer = await withdraw(id, { testNoteId, reason });
      onWithdrawn?.(answer, id);
      setOutcome((prev) =>
        prev ? { ...prev, reason: answer.reason, inbox: answer.inbox } : answer
      );
    } catch (err) {
      setSnack({
        status: true,
        msg: describeTestResultError(
          err,
          t('The report could not be marked entered in error.')
        ).message,
        severity: 'error'
      });
    } finally {
      setBusy(false);
    }
  };

  const closeDialog = () => {
    if (busy) return;
    setWithdrawing(null);
    setOutcome(null);
    setReason(null);
  };

  return (
    <Grid size={12}>
      <Box sx={PANEL_SX} data-testid="lab-reports">
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
          {t('Lab reports')}
        </Typography>
        {error && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {t('The lab reports for this Test could not be loaded.')}
          </Alert>
        )}
        {labReports.map((exported, i) => {
          const id = exported?.labReport?._id;
          return (
            <Box key={id || i}>
              {i > 0 && <Divider sx={{ mb: 1.5 }} />}
              <LabReportCard
                exported={exported}
                readOnly={readOnly}
                opening={opening === id}
                onOpen={() =>
                  onOpenReport && id
                    ? onOpenReport(String(id), exported)
                    : openReport(id)
                }
                onOpenInNewTab={
                  onOpenReport && id ? () => openReport(id) : undefined
                }
                onWithdraw={() => {
                  setReason(null);
                  setWithdrawing(id);
                }}
              />
            </Box>
          );
        })}
      </Box>

      <CustomDialog
        open={!!withdrawing}
        handleClose={closeDialog}
        title={
          outcome
            ? 'The report is marked entered in error'
            : 'Mark this report entered in error?'
        }
        titleAppearance="warning"
        minHeight={150}
        allowBackgroundClose={closeDialog}
      >
        {outcome ? (
          <Box>
            <WithdrawalOutcome answer={outcome} />
            <InboxOutcome
              answer={outcome}
              retrying={busy}
              onRetry={markEnteredInError}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 3 }}>
              <Button
                onClick={closeDialog}
                color="secondary"
                variant="contained"
              >
                {t('Close')}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box>
            <UnsavedChangesWarning
              items={
                withdrawing && typeof unsavedChangesFor === 'function'
                  ? unsavedChangesFor(withdrawing)
                  : []
              }
            />
            <Typography sx={{ fontWeight: 700 }} id="withdrawal-reason-label">
              {t('Why is this report entered in error?')}
            </Typography>
            <RadioGroup
              aria-labelledby="withdrawal-reason-label"
              value={reason || ''}
              onChange={(e) => setReason(e.target.value)}
              sx={{ mt: 0.5, mb: 2 }}
            >
              {WITHDRAWAL_REASON_CHOICES.map((choice) => (
                <FormControlLabel
                  key={choice.reason}
                  value={choice.reason}
                  disabled={busy}
                  control={<Radio size="small" />}
                  sx={{ alignItems: 'flex-start', mb: 1 }}
                  label={
                    <Box sx={{ pt: 0.75 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t(choice.label)}
                      </Typography>
                      <Typography
                        variant="caption"
                        component="div"
                        sx={{ color: 'text.secondary' }}
                      >
                        {t(choice.description)}
                      </Typography>
                    </Box>
                  }
                />
              ))}
            </RadioGroup>
            <Typography>
              {t(
                'What the report added to this Test and to the genotype record is removed, where nobody has changed it since and nothing else accounts for it. What it replaced is given back as the Test held it. Anything else stays, and you are shown what to review.'
              )}
            </Typography>
            <Typography sx={{ mt: 1 }}>
              {t(
                'The report and its record are kept, marked entered in error.'
              )}
            </Typography>
            {!reason && (
              <Typography
                variant="caption"
                component="div"
                sx={{ color: 'text.secondary', mt: 1.5 }}
              >
                {t('Choose why before marking it entered in error.')}
              </Typography>
            )}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 3,
                pt: 3
              }}
            >
              <Button
                onClick={closeDialog}
                color="secondary"
                variant="outlined"
                disabled={busy}
              >
                {t('Cancel')}
              </Button>
              <Button
                onClick={markEnteredInError}
                color="error"
                variant="contained"
                disabled={busy || !reason}
                startIcon={busy ? <CircularProgress size={16} /> : null}
              >
                {t('Mark entered in error')}
              </Button>
            </Box>
          </Box>
        )}
      </CustomDialog>
    </Grid>
  );
}
